import { useId } from 'react';
import type { MatrixAnswerValue } from '../../lib/question-blocks';
import type { RespondentField } from '../../lib/multimodal-contract';
import './MatrixQuestionControl.css';

interface MatrixQuestionControlProps {
  fieldId: string;
  matrix: NonNullable<RespondentField['matrix']>;
  selectionMode?: 'single' | 'multiple';
  value: MatrixAnswerValue;
  onChange: (value: MatrixAnswerValue) => void;
  onSubmit: (value: MatrixAnswerValue) => void;
  disabled: boolean;
  labelledBy: string;
  submitLabel: string;
}

const focus = 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-volt';
const action = `${focus} inline-flex cursor-pointer items-center justify-center rounded-lg px-4 py-3 text-sm disabled:cursor-not-allowed disabled:opacity-50`;

/** Use a table when it fits and compact row selectors in narrower containers. */
export function MatrixQuestionControl({ fieldId, matrix, selectionMode = 'single', value, onChange, onSubmit, disabled,
  labelledBy, submitLabel }: MatrixQuestionControlProps): React.ReactElement {
  const controlId = useId();
  const multiple = selectionMode === 'multiple';
  const tableEligible = matrix.columns.length <= 5;
  const valid = matrix.rows.length > 0 && matrix.columns.length > 0
    && Object.keys(value.selections).length === matrix.rows.length
    && matrix.rows.every(row => {
      const selected = value.selections[row.id];
      return multiple ? Array.isArray(selected) && selected.length > 0
        && new Set(selected).size === selected.length
        && selected.every(id => matrix.columns.some(column => column.id === id))
        : typeof selected === 'string' && matrix.columns.some(column => selected === column.id);
    });
  /** Keep obsolete saved column IDs correctable in the compact selector. */
  const selectValue = (rowId: string): string => {
    const selected = value.selections[rowId];
    return typeof selected === 'string' && matrix.columns.some(column => selected === column.id) ? selected : '';
  };
  /** Both layouts reflect the same stable row and column identities. */
  const checked = (rowId: string, columnId: string): boolean => {
    const selected = value.selections[rowId];
    return multiple ? Array.isArray(selected) && selected.includes(columnId) : selected === columnId;
  };
  /** Both responsive layouts update the same answer without submitting it. */
  const select = (rowId: string, columnId: string): void => {
    if (!columnId) return;
    const selected = value.selections[rowId];
    const current = Array.isArray(selected) ? selected : [];
    const next = multiple ? current.includes(columnId) ? current.filter(id => id !== columnId) : [...current, columnId] : columnId;
    onChange({ selections: { ...value.selections, [rowId]: next } });
  };

  return <div className="matrix-question-layout space-y-4">
    <div data-testid="matrix-mobile-controls" data-table-eligible={tableEligible}
      className="matrix-question-mobile" aria-labelledby={labelledBy}>
      {matrix.rows.map(row => multiple ? <fieldset key={row.id} className="matrix-question-mobile-row">
        <legend className="matrix-question-mobile-label">{row.label}</legend>
        {matrix.columns.map(column => <label key={column.id} className="flex min-h-11 min-w-0 cursor-pointer items-center gap-3 rounded-lg border border-border px-3 py-2 text-sm">
          <input type="checkbox" checked={checked(row.id, column.id)} disabled={disabled}
            name={`${fieldId}-${row.id}-${column.id}-compact`} className={`matrix-question-radio shrink-0 ${focus}`}
            onChange={() => select(row.id, column.id)} />
          <span className="min-w-0 [overflow-wrap:anywhere]">{column.label}</span>
        </label>)}
      </fieldset> : <label key={row.id} className="matrix-question-mobile-row">
        <span className="matrix-question-mobile-label">{row.label}</span>
        <select name={`${fieldId}-${row.id}-select`} aria-label={row.label} value={selectValue(row.id)} disabled={disabled}
          className={`${focus} matrix-question-select min-w-0 w-full [color-scheme:light] dark:[color-scheme:dark]`}
          onChange={event => select(row.id, event.target.value)}>
          <option value="" disabled>Choose</option>
          {matrix.columns.map(column => <option key={column.id} value={column.id}>{column.label}</option>)}
        </select>
      </label>)}
    </div>
    <div data-testid="matrix-table-controls" data-table-eligible={tableEligible} className="matrix-question-scroll">
      <table className="matrix-question-table" aria-labelledby={labelledBy}>
        <thead>
          <tr>
            <th aria-hidden="true" className="matrix-question-corner" />
            {matrix.columns.map((column, columnIndex) => {
              const columnId = `${controlId}-column-${columnIndex}`;
              return <th key={column.id} id={columnId} scope="col" className="matrix-question-column-heading">
                {column.label}
              </th>;
            })}
          </tr>
        </thead>
        <tbody>
          {matrix.rows.map((row, rowIndex) => {
            const rowId = `${controlId}-row-${rowIndex}`;
            return <tr key={row.id}>
              <th id={rowId} scope="row" className="matrix-question-row-heading">{row.label}</th>
              {matrix.columns.map((column, columnIndex) => {
                const columnId = `${controlId}-column-${columnIndex}`;
                return <td key={column.id} className="matrix-question-cell">
                  <label className="matrix-question-choice">
                    <input type={multiple ? 'checkbox' : 'radio'} name={`${fieldId}-${row.id}`} checked={checked(row.id, column.id)} disabled={disabled}
                      aria-labelledby={`${rowId} ${columnId}`} className={`matrix-question-radio ${focus}`}
                      onChange={() => select(row.id, column.id)} />
                  </label>
                </td>;
              })}
            </tr>;
          })}
        </tbody>
      </table>
    </div>
    <button type="button" disabled={disabled || !valid} className={`${action} button-accent`}
      onClick={() => onSubmit(value)}>{submitLabel}</button>
  </div>;
}
